import React, { FC, useState } from 'react';
import { Button, Drawer } from 'antd';
import { WarningCard } from 'common/components/WarningCard';
import { EMode } from 'common/const/common.enum';
import { Copy } from 'common/svg';
import { showMessage } from 'common/helpers/message.helper';
import { useLegalEntityContext } from 'common/context';
import { useAppDispatch, useAppSelector } from 'app/store/store.hooks';
import { ReactComponent as EditIcon } from 'app/assets/images/redesign/edit.svg';
import { ReactComponent as BinIcon } from 'app/assets/images/redesign/bin.svg';
import { deleteLegal, selectIsLegalLoading, selectLegal } from 'entities/LegalEntity/Legal.slice';

export const LegalEntityCard: FC = () => {
  const dispatch = useAppDispatch();

  const legal = useAppSelector(selectLegal);
  const isLegalLoading = useAppSelector(selectIsLegalLoading);

  const [openWarningCard, setOpenWarningCard] = useState<boolean>(false);
  const { setEditLegalEntityCardMode, openLegalEntityCard, setOpenLegalEntityCard, setOpenEditLegalEntityCard } =
    useLegalEntityContext();

  const onClose = () => {
    if (isLegalLoading) {
      return;
    }

    setOpenLegalEntityCard(false);
  };

  const onDeleteLegalEntityConfirmClick = () => {
    if (legal) {
      dispatch(
        deleteLegal({
          id: legal.id,
          onSuccess: () => {
            showMessage({
              content: `Юридическое лицо «${legal.name}» удалено.`,
              icon: <BinIcon className="icon-bin-dark-grey" />,
            });
            setOpenWarningCard(false);
          },
        }),
      );
    }
  };

  const copyInn = (e: any) => {
    e.stopPropagation();

    if (legal) {
      navigator.clipboard.writeText(legal.inn);
    }
  };

  const copyKpp = (e: any) => {
    e.stopPropagation();

    if (legal?.kpp) {
      navigator.clipboard.writeText(legal.kpp);
    }
  };

  if (!legal) {
    return null;
  }

  return (
    <>
      <Drawer
        rootClassName="redesign drawer legal-entity-card"
        destroyOnClose
        width={720}
        open={openLegalEntityCard}
        onClose={onClose}
        footer={[
          <Button
            key="delete"
            className="button-lg deleting"
            icon={<BinIcon className="icon-bin-pale-red" />}
            onClick={() => {
              setOpenLegalEntityCard(false);
              setOpenWarningCard(true);
            }}
          >
            Удалить
          </Button>,
          <Button
            key="edit"
            className="button-lg secondary"
            icon={<EditIcon className="icon-edit-dark-grey" />}
            onClick={() => {
              setEditLegalEntityCardMode(EMode.Edit);
              setOpenLegalEntityCard(false);
              setOpenEditLegalEntityCard(true);
            }}
          >
            Редактировать
          </Button>,
        ]}
      >
        <div className="drawer__body">
          <div className="drawer__title mb-14">{legal.name}</div>

          <div className="legal-entity-card__details">
            <div className="text-h4 legal-entity-card__details-title">Реквизиты юридического лица</div>

            <div className="legal-entity-card__details-table">
              <div className="legal-entity-card__details-table-row">
                <span className="text-body color-dark-grey">ИНН:</span>

                <div className="legal-entity-card__details-table-row-container">
                  <div className="text-body">{legal.inn}</div>
                  <Copy className="icon-dark-grey cursor-pointer" onClick={copyInn} />
                </div>
              </div>

              {legal.kpp && (
                <div className="legal-entity-card__details-table-row">
                  <span className="text-body color-dark-grey">КПП:</span>

                  <div className="legal-entity-card__details-table-row-container">
                    <span className="text-body">{legal.kpp}</span>
                    <Copy className="icon-dark-grey cursor-pointer" onClick={copyKpp} />
                  </div>
                </div>
              )}

              <div className="legal-entity-card__details-table-row">
                <span className="text-body color-dark-grey">НДС:</span>

                <div className="legal-entity-card__details-table-row-container">
                  <span className="text-body">{legal.isVatPayer ? 'Да' : 'Нет'}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Drawer>

      <WarningCard
        open={openWarningCard}
        content="Вы уверены, что хотите удалить юр. лицо?"
        subtitle="Юридическое лицо будет отвязано от подразделений и корзин пользователей."
        confirmBtnTitle="Удалить"
        loading={isLegalLoading}
        onConfirm={onDeleteLegalEntityConfirmClick}
        onClose={() => {
          setOpenWarningCard(false);
          setOpenLegalEntityCard(true);
        }}
      />
    </>
  );
};
